import { useEffect, useRef } from 'react'

/**
 * Run `callback` on a fixed interval, but skip ticks while the tab is hidden.
 * Also re-fetches immediately when the tab becomes visible again so the user
 * doesn't see stale data after returning.
 *
 * Cuts background polling traffic — a user with the tab in another window or
 * minimized stops hitting the API entirely until they come back.
 */
export function useVisibilityAwarePolling(
  callback: () => void | Promise<void>,
  intervalMs: number,
  enabled: boolean = true
): void {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  useEffect(() => {
    if (!enabled) return
    if (typeof document === 'undefined') return

    const tick = () => {
      if (document.visibilityState === 'hidden') return
      void callbackRef.current()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void callbackRef.current()
      }
    }

    const id = setInterval(tick, intervalMs)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [intervalMs, enabled])
}
